import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { manifesto } from '../mock';

export default function Manifesto() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  return (
    <section id="why-us" ref={containerRef} className="relative bg-[#0D1612]" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Pinned left */}
          <div className="lg:sticky lg:top-0 lg:h-screen flex flex-col justify-center py-20">
            <span className="inline-block text-emerald-400 font-semibold text-sm uppercase tracking-[0.25em] mb-6">
              / Why OutsourcedHub
            </span>
            <h2 className="font-display text-4xl md:text-6xl font-black text-white leading-[0.95] mb-8">
              We don&rsquo;t just fill<br />seats. We build<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-400">partnerships.</span>
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed max-w-md mb-10">
              Most agencies flood you with r&eacute;sum&eacute;s and disappear. We do the opposite &mdash; fewer, better matches, backed for the long haul.
            </p>

            <div className="flex gap-3">
              {manifesto.map((_, i) => {
                const start = i / manifesto.length;
                const end = (i + 1) / manifesto.length;
                return <Dot key={i} progress={scrollYProgress} start={start} end={end} />;
              })}
            </div>
          </div>

          {/* Scrolling right */}
          <div className="flex flex-col">
            {manifesto.map((item, index) => (
              <div key={item.number} className="min-h-screen flex items-center py-20">
                <motion.div
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, amount: 0.5 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="relative w-full"
                  data-testid={`manifesto-item-${index}`}
                >
                  <div className="font-display text-[10rem] md:text-[14rem] font-black leading-none text-transparent bg-clip-text bg-gradient-to-b from-emerald-500/30 to-transparent mb-4">
                    {item.number}
                  </div>
                  <div className="relative -mt-16 pl-2">
                    <div className="w-16 h-1.5 bg-emerald-400 rounded-full mb-8" />
                    <h3 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">{item.title}</h3>
                    <p className="text-xl text-slate-400 leading-relaxed max-w-lg">{item.description}</p>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Dot({ progress, start, end }) {
  const s0 = Math.max(0, start - 0.01);
  const e1 = Math.min(1, end + 0.01);
  const opacity = useTransform(progress, [s0, start, end, e1], [0.2, 1, 1, 0.2]);
  const width = useTransform(progress, [start, end], ['16px', '48px']);
  return <motion.div style={{ opacity, width }} className="h-2 bg-emerald-400 rounded-full" />;
}
